import { User, Role } from '../types';
import api from './api';

export const adminService = {
  async getUsers(orgID: number): Promise<User[]> {
    try {
      const response = await api.post('/api/v1/users', { orgID });
      return response.data;
    } catch (error: any) {
      console.error('Error fetching users:', error);
      throw new Error('Fehler beim Laden der Benutzer');
    }
  },

  async getRoles(orgID: number): Promise<Role[]> {
    try {
      const response = await api.post('/api/v1/roles', { orgID });
      return response.data;
    } catch (error: any) {
      console.error('Error fetching roles:', error);
      throw new Error('Fehler beim Laden der Rollen');
    }
  },

  async getRanks(): Promise<any[]> {
    try {
      const response = await api.post('/api/v1/ranks', {});
      return response.data;
    } catch (error: any) {
      console.error('Error fetching ranks:', error);
      throw new Error('Fehler beim Laden der Ränge');
    }
  },

  async getRoleUsers(orgID: number, roleID: number): Promise<User[]> {
    try {
      const response = await api.post('/api/v1/roles/users', { orgID, rolID: roleID });
      return response.data;
    } catch (error: any) {
      console.error('Error fetching role users:', error);
      throw new Error('Fehler beim Laden der Rollenbenutzer');
    }
  },

  async getRolePermissions(orgID: number, roleID: number): Promise<any> {
    try {
      const response = await api.post('/api/v1/roles/permissions', { orgID, rolID: roleID });
      return response.data;
    } catch (error: any) {
      console.error('Error fetching role permissions:', error);
      throw new Error('Fehler beim Laden der Rollenberechtigungen');
    }
  },

  async updateRole(roleID: number, orgID: number, roleData: { name: string; nameShort: string; description: string }): Promise<any> {
    try {
      const response = await api.post('/api/v1/roles/edit', {
        roleID,
        orgID,
        name: roleData.name,
        nameShort: roleData.nameShort,
        description: roleData.description
      });
      return response.data;
    } catch (error: any) {
      console.error('Error updating role:', error);
      throw new Error('Fehler beim Aktualisieren der Rolle');
    }
  },

  async updateRolePermissions(roleID: number, permissions: any[]): Promise<any> {
    try {
      const response = await api.put(`/api/v1/roles/${roleID}/permissions`, { permissions });
      return response.data;
    } catch (error: any) {
      console.error('Error updating role permissions:', error);
      throw new Error('Fehler beim Aktualisieren der Rollenberechtigungen');
    }
  },

  async addRolePermission(roleID: number, orgID: number, permissionID: number): Promise<any> {
    try {
      const response = await api.post(`/api/v1/roles/${roleID}/permissions/add`, {
        orgID,
        permissionID
      });
      return response.data;
    } catch (error: any) {
      console.error('Error adding permission to role:', error);
      throw new Error('Fehler beim Hinzufügen der Berechtigung');
    }
  },

  async removeRolePermission(roleID: number, orgID: number, permissionID: number): Promise<any> {
    try {
      const response = await api.post(`/api/v1/roles/${roleID}/permissions/remove`, {
        orgID,
        permissionID
      });
      return response.data;
    } catch (error: any) {
      console.error('Error removing permission from role:', error);
      throw new Error('Fehler beim Entfernen der Berechtigung');
    }
  },

  async createRole(orgID: number, roleData: {
    name: string;
    nameShort: string;
    description: string;
  }): Promise<any> {
    try {
      const response = await api.post('/api/v1/roles/add', {
        orgID,
        ...roleData
      });
      return response.data;
    } catch (error: any) {
      console.error('Error creating role:', error);
      throw new Error('Fehler beim Erstellen der Rolle');
    }
  },

  async deleteRole(roleID: number, orgID: number): Promise<any> {
    try {
      
      // First, get all users in this role
      const roleUsers = await this.getRoleUsers(orgID, roleID);
      
      // Remove all users from the role
      for (const user of roleUsers) {
        await this.removeUserFromRole(roleID, orgID, user.id);
      }
      
      // Get role permissions and remove them
      const rolePermissions = await this.getRolePermissions(orgID, roleID);
      
      // Remove all permissions from the role
      if (Array.isArray(rolePermissions)) {
        for (const permission of rolePermissions) {
          if (permission.assigned) {
            await this.removeRolePermission(roleID, orgID, permission.id);
          }
        }
      }
      
      // Now delete the role itself
      const response = await api.delete(`/api/v1/roles/${roleID}`, {
        data: { orgID }
      });
      return response.data;
    } catch (error: any) {
      console.error('Error deleting role:', error);
      throw new Error('Fehler beim Löschen der Rolle');
    }
  },

  async addUserToRole(roleID: number, orgID: number, userID: number): Promise<any> {
    try {
      const response = await api.post(`/api/v1/roles/${roleID}/users/add`, {
        orgID,
        userID
      });
      return response.data;
    } catch (error: any) {
      console.error('Error adding user to role:', error);
      throw new Error('Fehler beim Hinzufügen des Benutzers zur Rolle');
    }
  },

  async removeUserFromRole(roleID: number, orgID: number, userID: number): Promise<any> {
    try {
      const response = await api.delete(`/api/v1/roles/${roleID}/users/${userID}`, {
        data: { orgID }
      });
      return response.data;
    } catch (error: any) {
      console.error('Error removing user from role:', error);
      throw new Error('Fehler beim Entfernen des Benutzers aus der Rolle');
    }
  },

  async updateUser(userID: number, userData: {
    orgID: number;
    firstName: string;
    lastName: string;
    email: string;
    rankID?: number;
    password?: string;
    typeID?: number;
  }): Promise<any> {
    try {
      const response = await api.put(`/api/v1/users/${userID}`, userData);
      return response.data;
    } catch (error: any) {
      console.error('Error updating user:', error);
      throw new Error('Fehler beim Aktualisieren des Benutzers');
    }
  },

  async addUser(userData: {
    orgID: number;
    firstName: string;
    lastName: string;
    email: string;
    password: string;
    rankID: number;
    typeID?: number;
    languageID?: number;
    truckID?: number | null;
  }): Promise<any> {
    try {
      const response = await api.post('/api/v1/users/add', userData);
      return response.data;
    } catch (error: any) {
      console.error('Error adding user:', error);
      throw new Error('Fehler beim Hinzufügen des Benutzers');
    }
  },

  async deleteUser(userID: number, orgID: number): Promise<any> {
    try {
      const response = await api.delete(`/api/v1/users/${userID}`, {
        data: { orgID }
      });
      return response.data;
    } catch (error: any) {
      console.error('Error deleting user:', error);
      throw new Error('Fehler beim Löschen des Benutzers');
    }
  },

  // Locations
  async getLocations(orgID: number): Promise<any[]> {
    try {
      const response = await api.post('/api/v1/locations', { orgID: [orgID] });
      return response.data;
    } catch (error: any) {
      console.error('Error fetching locations:', error);
      throw new Error('Fehler beim Laden der Standorte');
    }
  },

  async addLocation(orgID: number, name: string, userID?: number): Promise<any> {
    try {
      const response = await api.post('/api/v1/locations/add', { 
        orgID, 
        name, 
        userID: userID || orgID // Fallback to orgID if userID not provided
      });
      return response.data;
    } catch (error: any) {
      console.error('Error adding location:', error);
      throw new Error('Fehler beim Hinzufügen des Standorts');
    }
  },

  async deleteLocation(locationID: number, orgID: number): Promise<any> {
    try {
      const response = await api.delete(`/api/v1/locations/${locationID}`, {
        data: { orgID }
      });
      return response.data;
    } catch (error: any) {
      console.error('Error deleting location:', error);
      throw new Error('Fehler beim Löschen des Standorts');
    }
  },

  // Radio Channels
  async getRadioChannels(orgID: number): Promise<any[]> {
    try {
      const response = await api.post('/api/v1/radiochannels', { orgID: [orgID] });
      return response.data;
    } catch (error: any) {
      console.error('Error fetching radio channels:', error);
      throw new Error('Fehler beim Laden der Funkkanäle');
    }
  },

  async addRadioChannel(orgID: number, name: string, userID?: number): Promise<any> {
    try {
      const response = await api.post('/api/v1/radiochannels/add', { 
        orgID, 
        name, 
        userID: userID || orgID // Fallback to orgID if userID not provided
      });
      return response.data;
    } catch (error: any) {
      console.error('Error adding radio channel:', error);
      throw new Error('Fehler beim Hinzufügen des Funkkanals');
    }
  },

  async deleteRadioChannel(channelID: number, orgID: number): Promise<any> {
    try {
      const response = await api.delete(`/api/v1/radiochannels/${channelID}`, {
        data: { orgID }
      });
      return response.data;
    } catch (error: any) {
      console.error('Error deleting radio channel:', error);
      throw new Error('Fehler beim Löschen des Funkkanals');
    }
  },

  // Tasks
  async getTasks(orgID: number): Promise<any[]> {
    try {
      const response = await api.post('/api/v1/tasks', { orgID });
      return response.data;
    } catch (error: any) {
      console.error('Error fetching tasks:', error);
      throw new Error('Fehler beim Laden der Tasks');
    }
  },

  async addTask(orgID: number, name: string): Promise<any> {
    try {
      const response = await api.post('/api/v1/tasks/add', { 
        orgID, 
        name 
      });
      return response.data;
    } catch (error: any) {
      console.error('Error adding task:', error);
      throw new Error('Fehler beim Hinzufügen der Task');
    }
  },

  async deleteTask(taskID: number, orgID: number): Promise<any> {
    try {
      const response = await api.post('/api/v1/tasks/delete', {
        orgID,
        taskID
      });
      return response.data;
    } catch (error: any) {
      console.error('Error deleting task:', error);
      throw new Error('Fehler beim Löschen der Task');
    }
  },

  // Feedbacks
  async getFeedbacks(orgID: number): Promise<any[]> {
    try {
      const response = await api.post('/api/v1/feedbacks', { orgID });
      return response.data;
    } catch (error: any) {
      console.error('Error fetching feedbacks:', error);
      throw new Error('Fehler beim Laden der Feedbacks');
    }
  },

  async addFeedback(orgID: number, text: string, userID?: number): Promise<any> {
    try {
      const response = await api.post('/api/v1/feedbacks/add', {
        orgID,
        text,
        userID: userID || orgID
      });
      return response.data;
    } catch (error: any) {
      console.error('Error adding feedback:', error);
      throw new Error('Fehler beim Hinzufügen des Feedbacks');
    }
  },

  async deleteFeedback(feedbackID: number, orgID: number): Promise<any> {
    try {
      const response = await api.delete(`/api/v1/feedbacks/${feedbackID}`, { data: { orgID } });
      return response.data;
    } catch (error: any) {
      // Treat 404 as success per API contract
      if (error?.response?.status === 404) {
        return { ok: true };
      }
      console.error('Error deleting feedback:', error);
      throw new Error('Fehler beim Löschen des Feedbacks');
    }
  },

  // Operation Types
  async getAllOperationTypes(): Promise<any[]> {
    try {
      // This should return ALL available operation types from the database
      const response = await api.post('/api/v1/operations/types/all', {});
      return response.data;
    } catch (error: any) {
      console.error('Error fetching all operation types:', error);
      throw new Error('Fehler beim Laden aller Einsatztypen');
    }
  },

  async getOperationTypes(orgID: number): Promise<any[]> {
    try {
      // This should return ALL available operation types from the database
      const response = await api.post('/api/v1/operations/types', { orgID });
      return response.data;
    } catch (error: any) {
      console.error('Error fetching operation types:', error);
      throw new Error('Fehler beim Laden der Einsatztypen');
    }
  },

  async getOperationTypesByOrg(orgID: number): Promise<any[]> {
    try {
      // This should return only the operation types assigned to the organization
      const response = await api.post('/api/v1/operations/types/by-org', { orgID });
      return response.data;
    } catch (error: any) {
      console.error('Error fetching operation types by org:', error);
      throw new Error('Fehler beim Laden der Organisations-Einsatztypen');
    }
  },

  async addOperationTypeToOrg(orgID: number, opsTypeID: number): Promise<any> {
    try {
      const response = await api.post('/api/v1/operations/types/add-to-org', {
        orgID,
        opsTypeID
      });
      return response.data;
    } catch (error: any) {
      console.error('Error adding operation type to org:', error);
      throw new Error('Fehler beim Hinzufügen des Einsatztyps');
    }
  },

  async removeOperationTypeFromOrg(orgID: number, opsTypeID: number): Promise<any> {
    try {
      const response = await api.post('/api/v1/operations/types/remove-from-org', {
        orgID,
        opsTypeID
      });
      return response.data;
    } catch (error: any) {
      console.error('Error removing operation type from org:', error);
      throw new Error('Fehler beim Entfernen des Einsatztyps');
    }
  }
}; 